import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { typography } from "@tKgpco/theme/styleguide";
import parse from "html-react-parser";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    linkCss?: InjectableCss;
    heading?: TypographyProps;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    wrapper: {
        css: css`
            div[data-test-selector="productDetails_htmlContent"] {
                font-size: ${TYPOGRAPHY.size.smaller};
                .product_description {
                    margin-bottom: 10px;
                    p {
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: 20px;
                    }
                    a,
                    span {
                        color: #3c75b5;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                    }
                }
            }
        `,
    },
    linkCss: {
        css: css`
            color: #3c75b5;
            font-weight: ${TYPOGRAPHY.weight.bold};
            cursor: pointer;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 20px;
            margin-bottom: 15px;
        `,
    },
    heading: {
        variant: "h4",
        css: css`
            color: ${COLORS.lightBlack};
            div[data-test-selector="productDetails_htmlContent"] {
                font-size: ${TYPOGRAPHY.size.smaller};
            }
        `,
    },
};

const styles = descriptionStyles;
const StyledSection = getStyledWrapper("section");

const ProductDetailsDescription: React.FC<Props> = ({ product }) => {
    if (!product.content) {
        return null;
    }
    const [readMore, setReadMore] = useState(false);
    const stripedHtml = product.content.htmlContent;

    const characterCount = stripedHtml.length;
    const subStripedHtml = stripedHtml.substring(0, 400);

    const fullDescription = (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.heading}>{translate("Product Description:")}</Typography>
            <div data-test-selector="productDetails_htmlContent">{parse(transformContentHtml(stripedHtml), parserOptions)}</div>
        </StyledWrapper>
    );

    const shortDescription = (
        <StyledWrapper {...styles.wrapper}>
            {characterCount > 0 && <Typography {...styles.heading}>{translate("Product Description:")}</Typography>}
            <div data-test-selector="productDetails_htmlContent">
                {parse(transformContentHtml(subStripedHtml), parserOptions)}
                {/* {characterCount > 400 ? <span>...</span> : null} */}
            </div>
        </StyledWrapper>
    );

    const linkName = readMore ? "Read Less" : "Read More";

    return (
        <StyledSection title="Description">
            {readMore ? fullDescription : shortDescription}
            {characterCount > 294 ? (
                <StyledWrapper
                    onClick={() => {
                        setReadMore(!readMore);
                    }}
                    {...styles.linkCss}
                >
                    {linkName}
                </StyledWrapper>
            ) : null}
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDescription),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
